import { type ReactNode } from "react";

import { cn } from "~/lib/utils";
import Paragraph from "~/componentsDS/Typography/Paragraph";

export type ComparisonCell = {
    /** Primary text of the cell. */
    text: ReactNode;
    /** Optional secondary line, rendered smaller on its own line (e.g. "(laser/electrolysis)"). */
    note?: ReactNode;
};

export type PriceCell = ComparisonCell & {
    /** Suffix appended to the amount with a regular weight (e.g. "/mo"). */
    period?: string;
};

export type ValueComparisonRow = {
    label: ComparisonCell;
    price: PriceCell;
    /** Visually emphasises the row (used for totals / savings). */
    highlight?: boolean;
    /** Overrides the default highlight background (defaults to "bg-gray100"). */
    highlightClassName?: string;
};

type Props = {
    rows: ValueComparisonRow[];
    className?: string;
};

const ValueComparisonTable = ({ rows, className }: Props) => (
    <div
        className={cn(
            "w-full flex flex-col overflow-hidden rounded-2xl text-primary500",
            "outline outline-1 outline-offset-[-1px] outline-gray200",
            "divide-y divide-gray200",
            className
        )}
    >
        {rows.map((row, index) => (
            <div
                key={index}
                className={cn(
                    "flex items-start justify-between gap-4 p-4",
                    row.highlight && (row.highlightClassName ?? "bg-gray100")
                )}
            >
                <Paragraph size="md" weight={row.highlight ? "500" : "400"}>
                    {row.label.text}
                    {row.label.note && (
                        <span className="block text-[14px]">{row.label.note}</span>
                    )}
                </Paragraph>

                <Paragraph size="md" weight="500" className="text-right">
                    {row.price.text}
                    {row.price.period && (
                        <span className="font-normal">{row.price.period}</span>
                    )}
                    {row.price.note && (
                        <span className="block text-[14px] font-normal">{row.price.note}</span>
                    )}
                </Paragraph>
            </div>
        ))}
    </div>
);

export default ValueComparisonTable;
